/**
 * `/boards` — the boards the caller may edit: one row per group they are a
 * member of (every group, for the superadmin), with or without text. Behind
 * `board.update`.
 *
 * A row shows the board's first line, so an editor sees which groups have one
 * without opening each. Editing is a dialog with a preview; emptying the text
 * removes the board from the members' dashboards.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Typography from '@mui/material/Typography';
import type { BoardDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { listBoards } from './api';
import { BoardDialog } from './BoardDialog';

/** The first line with text, without the Markdown marks that start a line. */
function firstLine(text: string): string {
  const line = text.split('\n').find((candidate) => candidate.trim() !== '') ?? '';
  return line.replace(/^\s*(?:#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s*)/, '').trim();
}

export default function BoardsPage() {
  const { t } = useTranslation(['board', 'common']);
  const list = useListQuery({ defaultSort: 'groupName' });
  const boards = useAsync(() => listBoards(list.query), [list.key]);
  const [editing, setEditing] = useState<BoardDto | null>(null);

  return (
    <>
      <PageHeader title={t('board:page.title')} description={t('board:page.description')} />

      <AsyncContent state={boards}>
        {({ boards: rows, page }) => (
          <EntityTable<BoardDto>
            rows={rows}
            rowKey={(board) => board.groupId}
            list={list}
            page={page}
            empty={{
              icon: <CampaignOutlinedIcon fontSize="inherit" />,
              title: t('board:page.emptyTitle'),
              description: t('board:page.emptyDescription'),
            }}
            columns={[
              {
                field: 'groupName',
                label: t('board:column.group'),
                sortable: true,
                width: 240,
                render: (board) => board.groupName,
              },
              {
                field: 'text',
                label: t('board:column.board'),
                render: (board) =>
                  board.text === '' ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('board:page.none')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" noWrap sx={{ maxWidth: 480 }}>
                      {firstLine(board.text)}
                    </Typography>
                  ),
              },
              {
                field: 'updatedAt',
                label: t('board:column.updated'),
                width: 160,
                render: (board) => board.updatedAt && <Timestamp value={board.updatedAt} />,
              },
            ]}
            actions={(board) => [
              {
                key: 'edit',
                label: t('common:action.edit'),
                icon: <EditOutlinedIcon fontSize="small" />,
                onClick: () => setEditing(board),
              },
            ]}
            actionsWidth={64}
          />
        )}
      </AsyncContent>

      <BoardDialog
        board={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          boards.reload();
        }}
      />
    </>
  );
}
